import { useEffect, useState, useCallback } from "react";
import api, { trackingBase } from "../lib/api";
import { PageHeader, Card, money, StatusBadge, Th, Td, DateRangeFilter, rangeParams } from "../components/shared";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus, Copy, Link2, Pencil, Trash2 } from "lucide-react";

const MACROS = ["sub1", "sub2", "sub3", "email_id", "utm_source", "utm_medium", "utm_campaign"];

const CampaignForm = ({ initial, sources, offers, onSave }) => {
  const [form, setForm] = useState(
    initial || { name: "", traffic_source_id: "", offer_id: "", cost_model: "CPC", cpc_cost: 0, geo: [], status: "active", notes: "" }
  );
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <div className="space-y-4">
      <div>
        <Label className="text-xs">Campaign Name</Label>
        <Input data-testid="campaign-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Keto Email Blast July" className="mt-1" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Traffic Source</Label>
          <Select value={form.traffic_source_id} onValueChange={(v) => set("traffic_source_id", v)}>
            <SelectTrigger data-testid="campaign-source-select" className="mt-1"><SelectValue placeholder="Select source" /></SelectTrigger>
            <SelectContent>{sources.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Target Offer</Label>
          <Select value={form.offer_id} onValueChange={(v) => set("offer_id", v)}>
            <SelectTrigger data-testid="campaign-offer-select" className="mt-1"><SelectValue placeholder="Select offer" /></SelectTrigger>
            <SelectContent>{offers.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Cost per Click ($)</Label>
          <Input data-testid="campaign-cpc-input" type="number" step="0.01" value={form.cpc_cost} onChange={(e) => set("cpc_cost", parseFloat(e.target.value) || 0)} className="mt-1" />
        </div>
        <div>
          <Label className="text-xs">Status</Label>
          <Select value={form.status} onValueChange={(v) => set("status", v)}>
            <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="paused">Paused</SelectItem></SelectContent>
          </Select>
        </div>
      </div>
      <Button data-testid="campaign-save-button" className="w-full bg-indigo-600 hover:bg-indigo-700" disabled={!form.name || !form.traffic_source_id || !form.offer_id} onClick={() => onSave(form)}>
        {initial ? "Update Campaign" : "Create Campaign"}
      </Button>
    </div>
  );
};

export default function Campaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [sources, setSources] = useState([]);
  const [offers, setOffers] = useState([]);
  const [networks, setNetworks] = useState([]);
  const [stats, setStats] = useState({});
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [linkFor, setLinkFor] = useState(null);
  const [selMacros, setSelMacros] = useState(["sub1", "email_id", "utm_source"]);
  const [rangeSel, setRangeSel] = useState({ range: "this_month" });

  const load = useCallback(() => {
    Promise.all([api.get("/campaigns"), api.get("/traffic-sources"), api.get("/offers"), api.get("/networks"), api.get(`/stats/entities?${rangeParams(rangeSel)}`)]).then(
      ([c, s, o, n, st]) => { setCampaigns(c.data); setSources(s.data); setOffers(o.data); setNetworks(n.data); setStats(st.data); }
    );
  }, [rangeSel]);
  useEffect(load, [load]);

  const save = async (form) => {
    if (editing) await api.put(`/campaigns/${editing.id}`, form);
    else await api.post("/campaigns", form);
    toast.success(editing ? "Campaign updated" : "Campaign created");
    setOpen(false); setEditing(null); load();
  };

  const del = async (id) => {
    await api.delete(`/campaigns/${id}`);
    toast.success("Campaign deleted"); load();
  };

  const trackingUrl = (c) => {
    const params = selMacros.map((m) => `${m}={${m}}`).join("&");
    const offer = offers.find((o) => o.id === c.offer_id);
    const net = networks.find((n) => n.id === offer?.network_id);
    return `${trackingBase(net)}/api/t/${c.id}${params ? "?" + params : ""}`;
  };

  const copyLink = (c) => {
    navigator.clipboard.writeText(trackingUrl(c));
    toast.success("Tracking link copied to clipboard");
  };

  return (
    <div data-testid="campaigns-page">
      <PageHeader
        title="Campaigns"
        subtitle="Tracking links, split traffic and performance per campaign"
        action={
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
            <DialogTrigger asChild>
              <Button data-testid="new-campaign-button" className="bg-indigo-600 hover:bg-indigo-700"><Plus className="h-4 w-4 mr-1" /> New Campaign</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit Campaign" : "Create Campaign"}</DialogTitle></DialogHeader>
              <CampaignForm key={editing?.id || "new"} initial={editing} sources={sources} offers={offers} onSave={save} />
            </DialogContent>
          </Dialog>
        }
      />

      <div className="mb-4 fade-up"><DateRangeFilter value={rangeSel} onChange={setRangeSel} /></div>

      <Card className="overflow-hidden fade-up">
        <div className="overflow-x-auto">
        <table className="w-full">
          <thead><tr><Th>Campaign</Th><Th>Source</Th><Th>Offer</Th><Th className="text-right">Clicks</Th><Th className="text-right">Conv.</Th><Th className="text-right">Revenue</Th><Th>Status</Th><Th className="text-right">Actions</Th></tr></thead>
          <tbody data-testid="campaigns-table-body">
            {campaigns.map((c) => {
              const st = stats[c.id] || { clicks: 0, conversions: 0, revenue: 0 };
              return (
                <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50/60">
                  <Td className="font-medium text-slate-900">{c.name}<div className="font-mono text-[10px] text-slate-400">{c.id}</div></Td>
                  <Td className="text-xs">{sources.find((s) => s.id === c.traffic_source_id)?.name || "—"}</Td>
                  <Td className="text-xs">{offers.find((o) => o.id === c.offer_id)?.name || "—"}</Td>
                  <Td className="text-right font-mono">{st.clicks.toLocaleString()}</Td>
                  <Td className="text-right font-mono">{st.conversions}</Td>
                  <Td className="text-right font-mono font-semibold text-emerald-600">{money(st.revenue)}</Td>
                  <Td><StatusBadge status={c.status} /></Td>
                  <Td className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button data-testid={`campaign-link-${c.id}`} size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => setLinkFor(c)}><Link2 className="h-3 w-3 mr-1" />Link</Button>
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => { setEditing(c); setOpen(true); }}><Pencil className="h-3 w-3" /></Button>
                      <Button data-testid={`campaign-delete-${c.id}`} size="sm" variant="ghost" className="h-7 w-7 p-0 text-rose-500" onClick={() => del(c.id)}><Trash2 className="h-3 w-3" /></Button>
                    </div>
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
        {campaigns.length === 0 && <div className="p-8 text-center text-sm text-slate-400">No campaigns yet. Create your first one.</div>}
      </Card>

      <Dialog open={!!linkFor} onOpenChange={(v) => !v && setLinkFor(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">Tracking URL Builder</DialogTitle></DialogHeader>
          {linkFor && (
            <div className="space-y-4">
              <div>
                <Label className="text-xs mb-2 block">Macro Parameters (replace {"{...}"} in your email platform / site)</Label>
                <div className="flex flex-wrap gap-1.5">
                  {MACROS.map((m) => (
                    <button
                      key={m}
                      data-testid={`macro-${m}`}
                      onClick={() => setSelMacros((s) => (s.includes(m) ? s.filter((x) => x !== m) : [...s, m]))}
                      className={`px-2.5 py-1 rounded-full text-[11px] font-mono border transition-colors ${selMacros.includes(m) ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-600 border-slate-200 hover:border-indigo-300"}`}
                    >
                      {`{${m}}`}
                    </button>
                  ))}
                </div>
              </div>
              <div className="bg-slate-900 rounded-lg p-3">
                <code data-testid="tracking-url-output" className="text-[11px] text-emerald-400 break-all">{trackingUrl(linkFor)}</code>
              </div>
              <Button data-testid="copy-tracking-url" className="w-full bg-indigo-600 hover:bg-indigo-700" onClick={() => copyLink(linkFor)}>
                <Copy className="h-4 w-4 mr-2" /> Copy Tracking Link
              </Button>
              <p className="text-[11px] text-slate-400">Visitors hitting this link get a unique click ID, are logged with IP / device / UTM data, then redirected to the offer URL with the click ID injected.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
